import React from 'react'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Paper,
  TextField,
  Typography
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import CloseIcon from '@mui/icons-material/Close'
import { SearchTourContentQuery, useSearchTourContentQuery } from '@typings/graphql'

type SearchResult = SearchTourContentQuery['searchTourContent'][number]

type Props = {
  tourId: string
}

const MIN_QUERY_LENGTH = 2
const DEBOUNCE_MS = 300
const GROUP_ORDER = ['section', 'nugget', 'content'] as const

// Highlight every case-insensitive occurrence of `needle` in `text`.
const highlight = (text: string, needle: string): React.ReactNode => {
  const trimmed = needle.trim()
  if (!trimmed) {
    return text
  }

  const escaped = trimmed.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const parts = text.split(new RegExp(`(${escaped})`, 'gi'))

  return parts.map((part, index) =>
    part.toLowerCase() === trimmed.toLowerCase()
      ? <mark key={index}>{part}</mark>
      : <React.Fragment key={index}>{part}</React.Fragment>
  )
}

export const TourContentSearch: React.FC<Props> = ({ tourId }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const [term, setTerm] = React.useState('')
  const [debouncedTerm, setDebouncedTerm] = React.useState('')

  React.useEffect(() => {
    const handle = setTimeout(() => setDebouncedTerm(term), DEBOUNCE_MS)
    return () => clearTimeout(handle)
  }, [term])

  const active = debouncedTerm.trim().length >= MIN_QUERY_LENGTH

  // no-cache: TourSearchResult is normalized by `id`, but one node can match on
  // several keys (same id) and each query has a different snippet — caching would
  // merge/overwrite them. Search results are ephemeral, so skip the cache.
  const { data, loading } = useSearchTourContentQuery({
    variables: { tourId, query: debouncedTerm },
    skip: !active,
    fetchPolicy: 'no-cache'
  })

  const results = data?.searchTourContent ?? []

  const grouped = React.useMemo(() => {
    const map: Record<string, SearchResult[]> = {}
    results.forEach((result) => {
      if (!map[result.kind]) {
        map[result.kind] = []
      }
      map[result.kind].push(result)
    })
    return map
  }, [results])

  const buildLink = (result: SearchResult): string => {
    const base = `/tour/${tourId}/mode/${result.modeId}/poi/${result.poiId}/section/${result.sectionId}`

    if (result.kind === 'nugget' && result.nuggetId) {
      return `${base}/nugget/${result.nuggetId}`
    }

    if (result.kind === 'content' && result.nuggetId && result.contentId) {
      return `${base}/nugget/${result.nuggetId}/contents/${result.contentId}`
    }

    return base
  }

  const openResult = (result: SearchResult) => {
    navigate(buildLink(result))
    setTerm('')
  }

  return (
    <Box sx={{ position: 'relative', maxWidth: 640 }}>
      <TextField
        fullWidth
        size="small"
        value={term}
        onChange={(event) => setTerm(event.target.value)}
        placeholder={t('search.placeholder')}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
          endAdornment: term
            ? (
              <InputAdornment position="end">
                {loading
                  ? <CircularProgress size={16} />
                  : (
                    <IconButton size="small" aria-label="clear" onClick={() => setTerm('')}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  )}
              </InputAdornment>
            )
            : undefined
        }}
      />

      {active && (
        <Paper
          elevation={3}
          sx={{
            position: 'absolute',
            zIndex: 20,
            mt: 0.5,
            width: '100%',
            maxHeight: 420,
            overflowY: 'auto'
          }}
        >
          {loading && results.length === 0
            ? (
              <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress size={20} />
              </Box>
            )
            : results.length === 0
              ? (
                <Box sx={{ p: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('search.noResults')}
                  </Typography>
                </Box>
              )
              : GROUP_ORDER.map((kind) => {
                const items = grouped[kind] ?? []
                if (items.length === 0) {
                  return null
                }

                return (
                  <List
                    key={kind}
                    dense
                    subheader={
                      <ListSubheader disableSticky>
                        {t(`search.group.${kind}`)} ({items.length})
                      </ListSubheader>
                    }
                  >
                    {items.map((result) => (
                      <ListItemButton
                        key={`${result.kind}-${result.id}-${result.matchedKey}`}
                        alignItems="flex-start"
                        onClick={() => openResult(result)}
                      >
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" component="span">
                                {highlight(result.snippet, debouncedTerm)}
                              </Typography>
                              <Chip
                                size="small"
                                variant="outlined"
                                label={result.contentType ?? result.matchedKey}
                              />
                            </Box>
                          }
                          secondary={result.breadcrumb}
                        />
                      </ListItemButton>
                    ))}
                  </List>
                )
              })}
        </Paper>
      )}
    </Box>
  )
}
